import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import 'rc-slider/assets/index.css';
import Slider from 'rc-slider';
import { ScreenService } from '@/utils/services/ScreenService.js';
import deviceService from '@/utils/services/DeviceService.js';
const Range = Slider.Range;

const SCHEDULE_TYPES = [
  { label: 'Regular Schedule', value: 'regular' },
  { label: 'Repetitive Schedule', value: 'repetitive' },
];

const defaultDays = [
  { name: 'Sun', index: 0, enabled: true, startHour: 0, endHour: 24 },
  { name: 'Mon', index: 1, enabled: true, startHour: 0, endHour: 24 },
  { name: 'Tue', index: 2, enabled: true, startHour: 0, endHour: 24 },
  { name: 'Wed', index: 3, enabled: true, startHour: 0, endHour: 24 },
  { name: 'Thu', index: 4, enabled: true, startHour: 0, endHour: 24 },
  { name: 'Fri', index: 5, enabled: true, startHour: 0, endHour: 24 },
  { name: 'Sat', index: 6, enabled: true, startHour: 0, endHour: 24 },
];

const ModifyScheduleModal = ({
  open,
  onClose,
  onUpdate,
  deviceName = '',
  screenOptions = [], // [{label, value, thumbUrl}]
  device,
  scheduleToEdit, // The schedule object to edit
  onScheduleUpdated,
}) => {
  const [scheduleType, setScheduleType] = useState('regular');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedScreen, setSelectedScreen] = useState('');
  const [error, setError] = useState('');
  const [days, setDays] = useState(defaultDays);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Initialize form with existing schedule data
  useEffect(() => {
    if (open && scheduleToEdit) {
      setScheduleType(scheduleToEdit.isRepetitive ? 'repetitive' : 'regular');
      setSelectedScreen(scheduleToEdit.screenId || '');
      
      if (scheduleToEdit.isRepetitive) {
        // Initialize days for repetitive schedule
        if (scheduleToEdit.days && scheduleToEdit.days.length > 0) {
          setDays(scheduleToEdit.days);
        } else {
          setDays(defaultDays);
        }
      } else {
        // Initialize dates for regular schedule
        if (scheduleToEdit.start) {
          const startDate = new Date(scheduleToEdit.start);
          setStartDate(startDate.toISOString().slice(0, 16));
        }
        if (scheduleToEdit.end) {
          const endDate = new Date(scheduleToEdit.end);
          setEndDate(endDate.toISOString().slice(0, 16));
        }
      }
      
      setError('');
      setSuccess(false);
    }
  }, [open, scheduleToEdit]);



  // Prevent background scroll when modal is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = originalOverflow; };
    }
  }, [open]);

  // Find selected screen object for preview
  const selectedScreenObj = screenOptions.find(s => s.value === selectedScreen);

  let previewUrl = null;
  if (selectedScreenObj) {
    previewUrl = ScreenService.getThumbnail(
      selectedScreenObj.imageUniversalUrl ||
      selectedScreenObj.imageUrl ||
      selectedScreenObj.thumbUniversalUrl ||
      selectedScreenObj.thumbUrl ||
      (selectedScreenObj.slidesData && selectedScreenObj.slidesData[0] && (selectedScreenObj.slidesData[0].imageUniversalUrl || selectedScreenObj.slidesData[0].imageUrl || selectedScreenObj.slidesData[0].thumbUniversalUrl || selectedScreenObj.slidesData[0].thumbUrl || selectedScreenObj.slidesData[0].videoUrl)) ||
      selectedScreenObj.videoUrl ||
      '',
      true
    );
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-[1350px] h-auto max-w-7xl flex flex-col overflow-visible">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 flex-shrink-0">
          <span className="font-bold text-2xl text-gray-900">Edit Schedule For The '{deviceName}' Device</span>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors" aria-label="Close">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content: Single column layout */}
        <div className="flex flex-row px-0 py-0 bg-gray-50 flex-1 overflow-hidden min-h-0 min-w-0 h-full">
          {/* Left: Schedule type display */}
          <div className="w-[330px] flex flex-col items-start gap-4 border-r border-gray-200 pl-8 pr-4 py-8 h-full bg-gray-50 flex-shrink-0">
            <div className="font-semibold text-lg text-gray-800 mb-2">Schedule Type</div>
            <div className="flex items-center gap-3 text-lg font-medium text-gray-700">
              <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              {scheduleType === 'regular' ? 'Regular Schedule' : 'Repetitive Schedule'}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              Schedule type cannot be changed when modifying an existing schedule.
            </div>
          </div>
          {/* Right: Content based on schedule type */}
          <div className="flex-1 min-w-0 px-10 py-8 bg-white overflow-visible">
            {scheduleType === 'regular' ? (
              <>
                {/* Date/Time Pickers */}
                <div className="flex gap-6 mb-6">
                  <div className="flex flex-col flex-1">
                    <label className="mb-2 text-lg font-medium text-gray-800">Start Date & Time <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      className="border border-gray-200 rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex flex-col flex-1">
                    <label className="mb-2 text-lg font-medium text-gray-800">End Date & Time <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      className="border border-gray-200 rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {error && <div className="text-red-500 mb-4 text-base">{error}</div>}
                {success && <div className="text-green-600 mb-4 text-base font-medium">✓ Schedule updated successfully!</div>}
                {/* Info Bullets */}
                <ul className="mb-6 text-blue-700 text-base space-y-1">
                  <li><span className="mr-2">✓</span>If the regular schedule type is selected the screen will be displayed on the device during the selected times.</li>
                  <li><span className="mr-2">✓</span>All the schedules for the device has to be of the same type (regular schedule or repetitive schedule).</li>
                  <li><span className="mr-2">✓</span>To create a schedule of a different type all existing schedules of the device has to be deleted first.</li>
                </ul>
                {/* Screen Select */}
                <div className="mb-4 flex items-center w-full gap-6 justify-between">
                  <div className="flex flex-col">
                    <label className="block text-lg font-medium text-gray-800 mb-2">Screen</label>
                    <select
                      className="w-60 border border-gray-300 rounded-lg px-4 py-2 text-base font-semibold bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      value={selectedScreen}
                      onChange={e => setSelectedScreen(e.target.value)}
                    >
                      {screenOptions.map(screen => (
                        <option key={screen.value} value={screen.value}>{screen.label}</option>
                      ))}
                    </select>
                  </div>
                  {selectedScreenObj && (
                    <img
                      src={previewUrl || 'https://via.placeholder.com/192x128?text=No+Preview'}
                      alt="Screen preview"
                      className="w-48 h-32 object-contain rounded-lg border border-gray-200 bg-gray-100 ml-auto"
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Repetitive Schedule UI */}
                <div className="mb-6 text-blue-700 text-base font-medium">Select days and time intervals for the repetitive schedule:</div>
                <div className="mb-6 w-full overflow-visible">
                  <table className="w-full border border-gray-200 rounded-lg bg-white">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700 text-base">
                        <th className="px-2 py-2 text-left w-14">Day</th>
                        <th className="px-2 py-2 text-center w-16">Enabled</th>
                        <th className="px-2 py-2 text-center w-[340px]">Time Range</th>
                        <th className="px-2 py-2 text-center w-28">Selected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((day, idx) => (
                        <tr key={day.index} className="border-t border-gray-100">
                          <td className="px-2 py-2 font-medium text-gray-800 w-14">{day.name}</td>
                          <td className="px-2 py-2 text-center w-16">
                            <input
                              type="checkbox"
                              checked={day.enabled}
                              onChange={e => {
                                const newDays = days.map((d, i) => i === idx ? { ...d, enabled: e.target.checked } : d);
                                setDays(newDays);
                              }}
                            />
                          </td>
                          <td className="px-2 py-4 text-center w-[340px]">
                            <div className="-mt-4">
                              <Range
                                min={0}
                                max={24}
                                step={1}
                                allowCross={false}
                                value={[day.startHour, day.endHour]}
                                disabled={!day.enabled}
                                onChange={([start, end]) => {
                                  const newDays = days.map((d, i) =>
                                    i === idx ? { ...d, startHour: start, endHour: end } : d
                                  );
                                  setDays(newDays);
                                }}
                                marks={{
                                  0: '0:00',
                                  6: '6:00',
                                  12: '12:00',
                                  18: '18:00',
                                  24: '24:00'
                                }}
                                trackStyle={[{ backgroundColor: '#facc15', height: 8 }]}
                                handleStyle={[
                                  { borderColor: '#facc15', backgroundColor: '#facc15', height: 24, width: 24, marginTop: -8 },
                                  { borderColor: '#facc15', backgroundColor: '#facc15', height: 24, width: 24, marginTop: -8 }
                                ]}
                                railStyle={{ backgroundColor: '#e5e7eb', height: 8 }}
                              />
                            </div>
                          </td>
                          <td className="px-2 py-2 text-center w-28 align-middle">
                            <span className="text-sm text-gray-700">
                              {`${String(day.startHour).padStart(2, '0')}:00 - ${String(day.endHour).padStart(2, '0')}:00`}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {error && <div className="text-red-500 mb-4 text-base">{error}</div>}
                {success && <div className="text-green-600 mb-4 text-base font-medium">✓ Schedule updated successfully!</div>}
                <ul className="mb-6 text-blue-700 text-base space-y-1">
                  <li><span className="mr-2">✓</span>If the repetitive schedule is selected the screen will be displayed on the device during the selected week days at the selected time intervals.</li>
                  <li><span className="mr-2">✓</span>All the schedules for the device has to be of the same type (regular schedule or repetitive schedule).</li>
                  <li><span className="mr-2">✓</span>To create a schedule of a different type all existing schedules of the device has to be deleted first.</li>
                </ul>
                {/* Screen Select */}
                <div className="mb-4 flex items-center w-full gap-6 justify-between">
                  <div className="flex flex-col">
                    <label className="block text-lg font-medium text-gray-800 mb-2">Screen</label>
                    <select
                      className="w-60 border border-gray-300 rounded-lg px-4 py-2 text-base font-semibold bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      value={selectedScreen}
                      onChange={e => setSelectedScreen(e.target.value)}
                    >
                      {screenOptions.map(screen => (
                        <option key={screen.value} value={screen.value}>{screen.label}</option>
                      ))}
                    </select>
                  </div>
                  {selectedScreenObj && (
                    <img
                      src={previewUrl || 'https://via.placeholder.com/192x128?text=No+Preview'}
                      alt="Screen preview"
                      className="w-48 h-32 object-contain rounded-lg border border-gray-200 bg-gray-100 ml-auto"
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {/* Actions */}
        <div className="flex justify-end gap-3 px-10 py-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
          <button
            className="px-5 py-3 rounded-lg text-lg text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onClose}
            disabled={saving}
          >Close</button>
          <button
            className="px-5 py-3 rounded-lg bg-yellow-500 text-white text-lg font-semibold hover:bg-yellow-600 transition-colors"
            disabled={saving || (scheduleType === 'regular' ? (!startDate || !endDate) : false)}
            onClick={async () => {
              if (!device) {
                setError('Device object is missing. Please select a device first.');
                return;
              }
              const deviceKey = device.key || device.id;
              if (!deviceKey) {
                setError('Device key is missing. Please try refreshing the page.');
                return;
              }

              setSaving(true);
              try {
                const updatedScheduleData = {
                  screenId: selectedScreen
                };

                if (scheduleType === 'regular') {
                  updatedScheduleData.start = new Date(startDate).getTime();
                  updatedScheduleData.end = new Date(endDate).getTime();
                  updatedScheduleData.isRepetitive = false;
                } else {
                  updatedScheduleData.isRepetitive = true;
                  updatedScheduleData.days = days;
                }

                const updatedDevice = await deviceService.updateSchedule(deviceKey, scheduleToEdit.id, updatedScheduleData);
                
                setSaving(false);
                setSuccess(true);
                
                // Refresh device data in parent component
                if (onScheduleUpdated) {
                  onScheduleUpdated(updatedDevice.schedule);
                }
                
                setTimeout(() => {
                  onUpdate && onUpdate(updatedDevice.schedule.find(s => s.id === scheduleToEdit.id));
                }, 1500);
              } catch (err) {
                setSaving(false);
                setError(err.message || 'Failed to update schedule. Please try again.');
              }
            }}
          >{saving ? 'Updating...' : 'Update Schedule'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModifyScheduleModal; 